import type { ComponentChildren } from 'preact'
import { useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { Button } from '~/components/primitives/Button'
import { DateField, TextField } from '~/components/primitives/Field'
import type { PhaseCorrection, PhaseHistoryPatch, PhaseRecord } from '~/models/phase'
import { PHASE_LABELS } from '~/models/phase'
import { NEXT_PHASE_ROUTE } from '~/routes'
import type { PhaseEndState } from '~/services/phaseService'
import { unwrapDate } from '~/types/dates'
import { formatCalendarDate } from '~/utils/format'
import styles from './PhaseHistorySection.module.css'

/**
 * The end-of-phase decision (D-020) and phase-history corrections (D-070).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THREE CHOICES, ALL EXPLICIT, NONE PRE-SELECTED.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * End, extend, or leave it running. The third is a real option presented as
 * such, not the absence of a decision — a screen offering only "end" and
 * "extend" would pressure a choice D-020 says the user is entitled not to make
 * yet.
 *
 * **Corrections cannot change a phase's type or target range**, and the form has
 * no field for either. That is the same freezing `PhaseHistoryPatch` enforces in
 * the type system, made visible: the summary shows them as fixed, with a line
 * saying why, so their absence reads as deliberate rather than forgotten.
 *
 * **Nothing is deletable.** There is no delete control here and no delete in the
 * repository behind it.
 */

type SaveHandler<T> = (input: T) => Promise<string | undefined>

interface PhaseHistorySectionProps {
  endState: PhaseEndState
  phases: readonly PhaseRecord[]
  onEndPhase: SaveHandler<{ actualEndDate: string; transitionReason?: string }>
  onExtendPhase: SaveHandler<string>
  onAcknowledge: () => Promise<string | undefined>
  onCorrect: SaveHandler<{ id: PhaseRecord['id']; patch: PhaseHistoryPatch }>
  today: string
}

export function PhaseHistorySection(props: PhaseHistorySectionProps) {
  return (
    <>
      <PhaseDecision {...props} />
      <NextPhaseInvitation endState={props.endState} phases={props.phases} />
      <PhaseHistoryList phases={props.phases} onCorrect={props.onCorrect} />
    </>
  )
}

/**
 * The fourth choice, offered once a phase has ended (D-103 §3).
 *
 * **Beside the decision rather than inside it**, which is what "offered once a
 * phase has ended rather than instead of ending it" means: `PhaseDecision` above
 * is the end/extend/leave choice for a phase that is still running, and this
 * appears only after one of those has been taken.
 *
 * Derived from the records rather than from a new prop. The condition is exactly
 * "no phase running, and one has finished" — which is what the service checks
 * before it will do anything, so the two cannot drift into disagreeing about
 * when the flow is available.
 */
function NextPhaseInvitation({
  endState,
  phases,
}: {
  endState: PhaseEndState
  phases: readonly PhaseRecord[]
}) {
  const [, navigate] = useLocation()

  if (endState.phase !== undefined) return null
  const finished = phases.some(
    (phase) => phase.status === 'completed' && phase.actualEndDate !== undefined,
  )
  if (!finished) return null

  return (
    <section aria-labelledby="next-phase-heading" class={styles.decision}>
      <h2 id="next-phase-heading" class={styles.sectionHeading}>
        No phase running
      </h2>
      <p class={styles.body}>
        Your project is still going. Project Sola will measure you again and work out which phase
        suits you now — <strong>nothing is created until you confirm it</strong>.
      </p>
      <div class={styles.choices}>
        <Button variant="secondary" onClick={() => navigate(NEXT_PHASE_ROUTE)}>
          Choose your next phase
        </Button>
      </div>
    </section>
  )
}

/**
 * The explicit decision about a running phase (D-020).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A PHASE CAN BE ENDED BEFORE ITS PLANNED END, AND UNTIL NOW IT COULD NOT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * This whole section used to return `null` until the planned end had passed, so
 * stopping for an injury, a holiday, or a cut going badly meant **first
 * extending the phase to today and then ending it** — which reads as the exact
 * opposite of the intent and leaves a corrected planned end in the history for
 * no reason. It is the third of the three defects D-103 §2 named.
 *
 * **Two states, one set of controls.** Before the planned end, the section is a
 * quiet offer with one action. After it, the D-020 warning appears and the three
 * choices open up. The button labels are identical on purpose — it is one
 * action, and the two states are mutually exclusive, so nothing has to
 * disambiguate them.
 *
 * **The register does not change with the state.** Ending early is not a failure
 * and must not be written as one: no "giving up", no warning colour, no
 * confirmation asking whether the user is sure. The form states what happens and
 * the user decides, exactly as the planned-end path does.
 */
function PhaseDecision({
  endState,
  onEndPhase,
  onExtendPhase,
  onAcknowledge,
  today,
}: PhaseHistorySectionProps) {
  const [open, setOpen] = useState<'none' | 'end' | 'extend'>('none')
  const [endDate, setEndDate] = useState(today)
  const [reason, setReason] = useState('')
  const [newPlannedEnd, setNewPlannedEnd] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const phase = endState.phase
  if (phase === undefined) return null
  const reached = endState.reachedPlannedEnd

  async function run(action: () => Promise<string | undefined>) {
    setBusy(true)
    setError(undefined)
    const message = await action()
    setBusy(false)
    if (message === undefined) setOpen('none')
    else setError(message)
  }

  return (
    <section aria-labelledby="phase-decision-heading" class={styles.decision}>
      <h2 id="phase-decision-heading" class={styles.sectionHeading}>
        {reached
          ? `${PHASE_LABELS[phase.type]} has reached its planned end`
          : `Ending ${PHASE_LABELS[phase.type].toLowerCase()} early`}
      </h2>

      {reached ? (
        <p class={styles.body}>
          It was planned to end on {formatCalendarDate(phase.plannedEndDate)}.{' '}
          <strong>Nothing has changed on its own</strong> — the phase is still running with the same
          targets. Choose what happens next when you are ready.
        </p>
      ) : (
        /* Neutral by design. Stopping early is an ordinary decision — an injury,
           a holiday, a cut that is not going well — and copy that treated it as
           quitting would make the honest choice the uncomfortable one. */
        <p class={styles.body}>
          It is planned to run until {formatCalendarDate(phase.plannedEndDate)}, and there is no
          need to end it before then. You can if something has changed — everything you have
          recorded is kept either way.
        </p>
      )}

      {open === 'none' ? (
        <div class={styles.choices}>
          <Button variant="secondary" onClick={() => setOpen('end')}>
            End this phase
          </Button>
          {/* Only at the planned end. Extending a phase with months still to run
              is not a decision the user is being asked to make, and offering it
              here would invite moving a target range's horizon for no reason. */}
          {reached ? (
            <Button variant="secondary" onClick={() => setOpen('extend')}>
              Extend it
            </Button>
          ) : null}
          {/* Leaving it alone is a real choice, so it is a control rather than
              the absence of one. It only silences the reminder, so it belongs
              only where there is a reminder to silence. */}
          {reached ? (
            <Button
              variant="secondary"
              onClick={() => run(onAcknowledge)}
              busy={busy}
              disabled={endState.acknowledged}
            >
              {endState.acknowledged ? 'Reminder hidden' : 'Leave it running for now'}
            </Button>
          ) : null}
        </div>
      ) : null}

      {open === 'end' ? (
        <DecisionForm
          busy={busy}
          error={error}
          onCancel={() => setOpen('none')}
          onSubmit={() =>
            run(() => onEndPhase({ actualEndDate: endDate, transitionReason: reason }))
          }
          submitLabel="End this phase"
        >
          <DateField
            label="The day it actually ended"
            value={endDate}
            onInput={setEndDate}
            hint="Often today, but it can be earlier if you stopped a while ago."
          />
          <TextField
            label="Why it ended (optional)"
            value={reason}
            onInput={setReason}
            hint="For your own history — for example, moving to maintenance, or the event passed."
          />
          {/* D-103 §2. This used to promise "until you set one up" when there
              was no such path — the only control the app then offered ran first-
              run setup and wrote a second active project. The sentence now
              describes something that exists. */}
          <p class={styles.note}>
            Ending a phase does not start another one. Your project keeps running, and Project Sola
            will offer to work out what comes next — after it has measured you again.
          </p>
        </DecisionForm>
      ) : null}

      {open === 'extend' ? (
        <DecisionForm
          busy={busy}
          error={error}
          onCancel={() => setOpen('none')}
          onSubmit={() => run(() => onExtendPhase(newPlannedEnd))}
          submitLabel="Extend the phase"
        >
          <DateField
            label="New planned end date"
            value={newPlannedEnd}
            onInput={setNewPlannedEnd}
            hint="The targets and the weekly range stay exactly as they are."
          />
        </DecisionForm>
      ) : null}
    </section>
  )
}

function DecisionForm({
  children,
  onSubmit,
  onCancel,
  submitLabel,
  busy,
  error,
}: {
  children: ComponentChildren
  onSubmit: () => void
  onCancel: () => void
  submitLabel: string
  busy: boolean
  error: string | undefined
}) {
  return (
    <form
      class={styles.form}
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      {children}
      {error === undefined ? null : (
        <p class={styles.problem} role="alert">
          {error}
        </p>
      )}
      <div class={styles.actions}>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" busy={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

function PhaseHistoryList({
  phases,
  onCorrect,
}: {
  phases: readonly PhaseRecord[]
  onCorrect: PhaseHistorySectionProps['onCorrect']
}) {
  if (phases.length === 0) return null

  return (
    <section aria-labelledby="phase-history-heading">
      <h2 id="phase-history-heading" class={styles.sectionHeading}>
        Phase history
      </h2>
      <p class={styles.body}>
        Every phase you have run, newest first. Dates, notes and the reason a phase ended can be
        corrected; the type and the weekly target range cannot, because changing them would rewrite
        whether past weeks were on track. Nothing here can be deleted.
      </p>

      <ul class={styles.list}>
        {phases.map((phase) => (
          <li key={phase.id}>
            <PhaseHistoryEntry phase={phase} onCorrect={onCorrect} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function PhaseHistoryEntry({
  phase,
  onCorrect,
}: {
  phase: PhaseRecord
  onCorrect: PhaseHistorySectionProps['onCorrect']
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [startDate, setStartDate] = useState(unwrapDate(phase.startDate))
  const [endDate, setEndDate] = useState(
    phase.actualEndDate === undefined ? '' : unwrapDate(phase.actualEndDate),
  )
  const [notes, setNotes] = useState(phase.notes ?? '')
  const [reason, setReason] = useState(phase.transitionReason ?? '')

  async function save() {
    setBusy(true)
    setError(undefined)
    const message = await onCorrect({
      id: phase.id,
      patch: {
        startDate,
        ...(phase.status === 'completed' || endDate !== '' ? { actualEndDate: endDate } : {}),
        notes,
        transitionReason: reason,
      },
    })
    setBusy(false)
    if (message === undefined) setEditing(false)
    else setError(message)
  }

  return (
    <article class={styles.entry}>
      <h3 class={styles.entryHeading}>
        {PHASE_LABELS[phase.type]}
        <span class={styles.status}>
          {phase.status === 'active' ? ' · running now' : ' · finished'}
        </span>
      </h3>

      {editing ? (
        <form
          class={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
        >
          <DateField label="Start date" value={startDate} onInput={setStartDate} />
          {phase.status === 'completed' ? (
            <DateField label="End date" value={endDate} onInput={setEndDate} />
          ) : null}
          <TextField label="Notes" value={notes} onInput={setNotes} />
          <TextField label="Why it ended" value={reason} onInput={setReason} />

          <p class={styles.note}>
            The phase type and its weekly target range are not editable. Changing a past target
            range would change whether your recorded weeks were on track, and the answers Project
            Sola already gave you stay as they were.
          </p>

          {error === undefined ? null : (
            <p class={styles.problem} role="alert">
              {error}
            </p>
          )}
          <div class={styles.actions}>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button type="submit" busy={busy}>
              Save correction
            </Button>
          </div>
        </form>
      ) : (
        <>
          <dl class={styles.detail}>
            <Row term="Ran">
              {formatCalendarDate(phase.startDate)} to{' '}
              {phase.actualEndDate === undefined
                ? `${formatCalendarDate(phase.plannedEndDate)} (planned)`
                : formatCalendarDate(phase.actualEndDate)}
            </Row>
            <Row term="Weekly target">
              {phase.targetWeeklyChangeMinPercent}% to {phase.targetWeeklyChangeMaxPercent}% · fixed
            </Row>
            {phase.transitionReason === undefined ? null : (
              <Row term="Why it ended">{phase.transitionReason}</Row>
            )}
            {phase.notes === undefined ? null : <Row term="Notes">{phase.notes}</Row>}
          </dl>

          <CorrectionHistory corrections={phase.corrections} />

          <Button
            variant="secondary"
            onClick={() => setEditing(true)}
            label={`Correct the ${PHASE_LABELS[phase.type]} phase`}
            fullWidth
          >
            Correct
          </Button>
        </>
      )}
    </article>
  )
}

const FIELD_LABELS: Readonly<Record<PhaseCorrection['field'], string>> = {
  startDate: 'Start date',
  actualEndDate: 'End date',
  notes: 'Notes',
  transitionReason: 'Why it ended',
}

/**
 * What was corrected, and what it replaced (D-070).
 *
 * Shown rather than hidden, so a corrected record is visibly a corrected record.
 * Presenting a late amendment as though it were the original is exactly what
 * D-070 refused when it rejected a silent overwrite.
 */
function CorrectionHistory({
  corrections,
}: {
  corrections: readonly PhaseCorrection[] | undefined
}) {
  if (corrections === undefined || corrections.length === 0) return null

  return (
    <details class={styles.corrections}>
      <summary class={styles.correctionsSummary}>
        Corrected {corrections.length === 1 ? 'once' : `${corrections.length} times`}
      </summary>
      <ul class={styles.correctionList}>
        {corrections.map((correction) => (
          <li key={`${correction.field}-${correction.correctedAt}`}>
            {FIELD_LABELS[correction.field]}:{' '}
            {correction.from === undefined ? 'added' : `was ${correction.from}`}
            {correction.to === undefined ? ', removed' : `, now ${correction.to}`}
          </li>
        ))}
      </ul>
    </details>
  )
}

function Row({ term, children }: { term: string; children: ComponentChildren }) {
  return (
    <div class={styles.row}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  )
}
